import { useState } from "react";

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { type AuditLogFilters, exportAuditLogsCsv, listAuditLogs } from "@/api/audit";
import { PageHeader } from "@/components/PageHeader";

const KNOWN_ACTIONS = [
  "LOGIN_SUCCESS",
  "LOGIN_FAILED",
  "PLANNING_PUBLISHED",
  "ABSENCE_REPORTED",
  "ABSENCE_TAKEN_OVER",
];

const ACTION_COLOR: Record<string, "success" | "error" | "warning" | "info" | "default"> = {
  LOGIN_SUCCESS: "success",
  LOGIN_FAILED: "error",
  PLANNING_PUBLISHED: "info",
  ABSENCE_REPORTED: "warning",
  ABSENCE_TAKEN_OVER: "success",
};

export function AuditLogPage() {
  const { t, i18n } = useTranslation();
  const [filters, setFilters] = useState<AuditLogFilters>({ limit: 25, offset: 0 });

  const query = useQuery({
    queryKey: ["audit-logs", filters],
    queryFn: () => listAuditLogs(filters),
  });

  const downloadCsv = async () => {
    const { limit: _l, offset: _o, ...rest } = filters;
    void _l;
    void _o;
    try {
      const blob = await exportAuditLogsCsv({ ...rest, limit: 10000 });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "")}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const handleAction = (action: string) =>
    setFilters((f) => ({ ...f, action: action || undefined, offset: 0 }));

  const handleEmail = (email: string) => {
    // We don't expose an email filter on the API yet, so use it as a free-text matcher client-side
    setFilters((f) => ({ ...f, offset: 0 }));
    void email;
  };
  void handleEmail;

  const total = query.data?.total ?? 0;
  const limit = filters.limit ?? 25;
  const offset = filters.offset ?? 0;
  const hasPrev = offset > 0;
  const hasNext = offset + limit < total;

  const fmtDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString(i18n.language, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  if (query.error) {
    const err = query.error as { response?: { status?: number } };
    if (err.response?.status === 403) {
      return (
        <Box>
          <PageHeader title={t("audit.title")} subtitle={t("audit.subtitle")} helpTerm="audit" />
          <Alert severity="warning">{t("audit.forbidden")}</Alert>
        </Box>
      );
    }
  }

  return (
    <Box>
      <PageHeader
        title={t("audit.title")}
        subtitle={t("audit.subtitle")}
        actions={
          <>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={downloadCsv}
            >
              {t("audit.exportCsv")}
            </Button>
            <IconButton onClick={() => query.refetch()} aria-label={t("audit.refresh")}>
              <RefreshIcon />
            </IconButton>
          </>
        }
      />

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small"
          label={t("audit.search")}
          placeholder={t("audit.searchPlaceholder")}
          value={filters.q ?? ""}
          onChange={(e) =>
            setFilters((f) => ({
              ...f,
              q: e.target.value || undefined,
              offset: 0,
            }))
          }
          sx={{ minWidth: 220 }}
        />
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>{t("audit.filterAction")}</InputLabel>
          <Select
            label={t("audit.filterAction")}
            value={filters.action ?? ""}
            onChange={(e) => handleAction(e.target.value)}
          >
            <MenuItem value="">
              <em>{t("audit.allActions")}</em>
            </MenuItem>
            {KNOWN_ACTIONS.map((a) => (
              <MenuItem key={a} value={a}>
                {a}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label={t("audit.filterFrom")}
          type="datetime-local"
          value={filters.from ?? ""}
          onChange={(e) =>
            setFilters((f) => ({
              ...f,
              from: e.target.value ? `${e.target.value}:00Z` : undefined,
              offset: 0,
            }))
          }
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          label={t("audit.filterTo")}
          type="datetime-local"
          value={filters.to ?? ""}
          onChange={(e) =>
            setFilters((f) => ({
              ...f,
              to: e.target.value ? `${e.target.value}:00Z` : undefined,
              offset: 0,
            }))
          }
          InputLabelProps={{ shrink: true }}
        />
        <Button
          variant="outlined"
          onClick={() => setFilters({ limit: 25, offset: 0 })}
        >
          {t("audit.clearFilters")}
        </Button>
      </Stack>

      {query.isLoading ? (
        <CircularProgress />
      ) : (
        <Paper variant="outlined" sx={{ overflow: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("audit.col.date")}</TableCell>
                <TableCell>{t("audit.col.actor")}</TableCell>
                <TableCell>{t("audit.col.action")}</TableCell>
                <TableCell>{t("audit.col.resource")}</TableCell>
                <TableCell>{t("audit.col.ip")}</TableCell>
                <TableCell>{t("audit.col.payload")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(query.data?.items ?? []).map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell sx={{ whiteSpace: "nowrap", fontFamily: "monospace" }}>
                    {fmtDate(row.created_at)}
                  </TableCell>
                  <TableCell>{row.actor_email ?? "—"}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={ACTION_COLOR[row.action] ?? "default"}
                      label={row.action}
                    />
                  </TableCell>
                  <TableCell>
                    {row.resource_type ? (
                      <>
                        <Typography variant="caption" color="text.secondary">
                          {row.resource_type}
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 11 }}>
                          {row.resource_id?.slice(0, 8) ?? "—"}
                        </Typography>
                      </>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 11 }}>
                    {row.ip_address ?? "—"}
                  </TableCell>
                  <TableCell>
                    {row.payload ? (
                      <Typography
                        variant="body2"
                        component="pre"
                        sx={{
                          fontFamily: "monospace",
                          fontSize: 11,
                          m: 0,
                          maxWidth: 360,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-all",
                        }}
                      >
                        {JSON.stringify(row.payload, null, 0)}
                      </Typography>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {(query.data?.items ?? []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Alert severity="info" sx={{ my: 1 }}>
                      {t("audit.empty")}
                    </Alert>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
        <IconButton
          disabled={!hasPrev}
          onClick={() => setFilters((f) => ({ ...f, offset: Math.max(0, offset - limit) }))}
        >
          <ChevronLeftIcon />
        </IconButton>
        <Typography variant="body2">
          {t("audit.pagination", {
            from: total === 0 ? 0 : offset + 1,
            to: Math.min(offset + limit, total),
            total,
          })}
        </Typography>
        <IconButton
          disabled={!hasNext}
          onClick={() => setFilters((f) => ({ ...f, offset: offset + limit }))}
        >
          <ChevronRightIcon />
        </IconButton>
      </Stack>
    </Box>
  );
}
